import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Sidebar } from './components/Sidebar';
import { Player } from './components/Player';
import { VideoMiniPlayer } from './components/VideoMiniPlayer';
import { PlayerProvider } from './context/PlayerContext';
import { VideoProvider } from './context/VideoContext';
import { SocialProvider } from './context/SocialContext';
import { AuthProvider } from './context/AuthContext';
import { GuestProvider } from './context/GuestContext';
import { UserProvider } from './context/UserContext';
import { PricingProvider } from './context/PricingContext';
import { ReferralProvider } from './context/ReferralContext';
import { ShareProvider } from './context/ShareContext';
import { UploadQueueProvider } from './context/UploadQueueContext';
import { CartProvider } from './context/CartContext';
import { AuthGuard } from './components/AuthGuard';
import { LoginForm } from './components/LoginForm';
import { SignUpForm } from './components/SignUpForm';
import { ForgotPassword } from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { Footer } from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import { MobileBottomNav } from './components/MobileBottomNav';
import { OnboardingModal } from './components/OnboardingModal';
import { CartDrawer } from './components/CartDrawer';
import { Toaster } from 'sonner';

const Home = React.lazy(() => import('./pages/Home'));
const Search = React.lazy(() => import('./pages/Search'));
const Library = React.lazy(() => import('./pages/Library'));
const Discover = React.lazy(() => import('./pages/Discover'));
const Community = React.lazy(() => import('./pages/Community'));
const Rewards = React.lazy(() => import('./pages/Rewards'));
const Creator = React.lazy(() => import('./pages/Creator'));
const Playlist = React.lazy(() => import('./pages/Playlist'));
const Song = React.lazy(() => import('./pages/Song'));
const Album = React.lazy(() => import('./pages/Album'));
const Podcast = React.lazy(() => import('./pages/Podcast'));
const ArtistDashboard = React.lazy(() => import('./pages/ArtistDashboard'));
const BecomeArtist = React.lazy(() => import('./pages/BecomeArtist'));
const ArtistPending = React.lazy(() => import('./pages/ArtistPending'));
const UploadMusic = React.lazy(() => import('./pages/UploadMusic'));
const UploadSong = React.lazy(() => import('./pages/UploadSong'));
const UploadAlbum = React.lazy(() => import('./pages/UploadAlbum'));
const UploadVideo = React.lazy(() => import('./pages/UploadVideo'));
const Settings = React.lazy(() => import('./pages/Settings'));
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminPanel = React.lazy(() => import('./pages/admin/AdminPanel'));
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const SubmitMusic = React.lazy(() => import('./pages/SubmitMusic'));
const Artists = React.lazy(() => import('./pages/Artists'));
const ArtistProfile = React.lazy(() => import('./pages/ArtistProfile'));
const ClaimArtist = React.lazy(() => import('./pages/ClaimArtist'));
const Partners = React.lazy(() => import('./pages/Partners'));
const Charts = React.lazy(() => import('./pages/Charts'));
const CreatorPortal = React.lazy(() => import('./pages/CreatorPortal'));
const Subscription = React.lazy(() => import('./pages/Subscription'));
const Video = React.lazy(() => import('./pages/Video'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const About = React.lazy(() => import('./pages/About'));
const Help = React.lazy(() => import('./pages/Help'));
const Contact = React.lazy(() => import('./pages/Contact'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const SmartLink = React.lazy(() => import('./pages/SmartLink'));
const ArtistLinkPage = React.lazy(() => import('./pages/ArtistLinkPage'));
const Explore = React.lazy(() => import('./pages/Explore'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const ReleasesLanding = React.lazy(() => import('./pages/tools/ReleasesLanding'));
const FanChatLanding = React.lazy(() => import('./pages/tools/FanChatLanding'));
const PaymentsLanding = React.lazy(() => import('./pages/tools/PaymentsLanding'));
const AIStudio = React.lazy(() => import('./pages/AIStudio'));
const Marketplace = React.lazy(() => import('./pages/Marketplace'));
const Distribution = React.lazy(() => import('./pages/Distribution'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const WatchHistory = React.lazy(() => import('./pages/WatchHistory'));
const Articles = React.lazy(() => import('./pages/Articles'));
const ArticleDetail = React.lazy(() => import('./pages/ArticleDetail'));
const AffiliateDashboard = React.lazy(() => import('./pages/AffiliateDashboard'));
const AdminRevenue = React.lazy(() => import('./pages/AdminRevenue'));
const Live = React.lazy(() => import('./pages/Live'));
const LivestreamEvents = React.lazy(() => import('./pages/LivestreamEvents'));
const Testimonies = React.lazy(() => import('./pages/Testimonies'));
const PrayerWall = React.lazy(() => import('./pages/PrayerWall'));
const Ministry = React.lazy(() => import('./pages/Ministry'));
const Events = React.lazy(() => import('./pages/Events'));
const CommandCenter = React.lazy(() => import('./pages/CommandCenter'));
const BrandDashboard = React.lazy(() => import('./pages/BrandDashboard'));
const Donate = React.lazy(() => import('./pages/Donate'));
const TVHome = React.lazy(() => import('./pages/tv/TVHome'));
const TVShow = React.lazy(() => import('./pages/tv/TVShow'));
const TVSchedule = React.lazy(() => import('./pages/tv/TVSchedule'));
const TVEvents = React.lazy(() => import('./pages/tv/TVEvents'));
const TVBadges = React.lazy(() => import('./pages/tv/TVBadges'));
const TVSearch = React.lazy(() => import('./pages/tv/TVSearch'));
const TVCreator = React.lazy(() => import('./pages/tv/TVCreator'));
const AlbumStorefront = React.lazy(() => import('./pages/AlbumStorefront'));
const AlbumDownload = React.lazy(() => import('./pages/AlbumDownload'));
const CreatePlaylist = React.lazy(() => import('./pages/CreatePlaylist'));
const FanStore = React.lazy(() => import('./pages/FanStore'));
const FanLoyalty = React.lazy(() => import('./pages/FanLoyalty'));
const DropPage = React.lazy(() => import('./pages/DropPage'));

const AppLayout: React.FC = () => (
  <div className="min-h-screen bg-royal-bg text-royal-text">
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 lg:ml-72 pb-32">
        <div className="max-w-screen-2xl mx-auto px-8 lg:px-12 py-8">
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>
    </div>
    <Player />
    <VideoMiniPlayer />
    <MobileBottomNav />
    <CartDrawer />
    <OnboardingModal />
    <Footer />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <ErrorBoundary>
          <AuthProvider>
            <UserProvider>
              <PricingProvider>
                <ReferralProvider>
                  <ShareProvider>
                    <UploadQueueProvider>
                      <CartProvider>
                        <PlayerProvider>
                          <VideoProvider>
                            <SocialProvider>
                              <GuestProvider>
                                <Toaster position="top-center" expand={true} richColors />
                                <Suspense fallback={<LoadingSpinner />}>
                                  <Routes>
                                    <Route path="/login" element={<LoginForm />} />
                                    <Route path="/signup" element={<SignUpForm />} />
                                    <Route path="/forgot-password" element={<ForgotPassword />} />
                                    <Route path="/reset-password" element={<ResetPassword />} />
                                    <Route path="/admin/login" element={<AdminLogin />} />
                                    <Route path="/admin" element={<AdminLayout />} />
                                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                                    <Route path="/admin/panel" element={<AdminPanel />} />
                                    <Route path="/buy/:slug" element={<AlbumStorefront />} />
                                    <Route path="/download/:token" element={<AlbumDownload />} />
                                    <Route path="/link/:slug" element={<SmartLink />} />
                                    <Route path="/l/:slug" element={<ArtistLinkPage />} />

                                    <Route element={<AppLayout />}>
                                      <Route path="/" element={<Home />} />
                                      <Route path="/explore" element={<Explore />} />
                                      <Route path="/pricing" element={<Pricing />} />
                                      <Route path="/releases" element={<ReleasesLanding />} />
                                      <Route path="/fan-chat" element={<FanChatLanding />} />
                                      <Route path="/payments" element={<PaymentsLanding />} />
                                      <Route path="/search" element={<Search />} />
                                      <Route path="/discover" element={<Discover />} />
                                      <Route path="/charts" element={<Charts />} />
                                      <Route path="/artists" element={<Artists />} />
                                      <Route path="/artist/:slug" element={<ArtistProfile />} />
                                      <Route path="/claim-artist/:artistId" element={<ClaimArtist />} />
                                      <Route path="/partners" element={<Partners />} />
                                      <Route path="/creator/:id" element={<Creator />} />
                                      <Route path="/playlist/:id" element={<Playlist />} />
                                      <Route path="/song/:id" element={<Song />} />
                                      <Route path="/album/:id" element={<Album />} />
                                      <Route path="/podcast/:id" element={<Podcast />} />
                                      <Route path="/video/:id" element={<Video />} />
                                      <Route path="/privacy" element={<PrivacyPolicy />} />
                                      <Route path="/terms" element={<TermsOfService />} />
                                      <Route path="/about" element={<About />} />
                                      <Route path="/help" element={<Help />} />
                                      <Route path="/contact" element={<Contact />} />
                                      <Route path="/become-artist" element={<BecomeArtist />} />
                                      <Route path="/store/:slug" element={<FanStore />} />
                                      <Route path="/fan-loyalty/:slug" element={<FanLoyalty />} />
                                      <Route path="/drop/:slug" element={<DropPage />} />
                                      <Route path="/marketplace" element={<Marketplace />} />
                                      <Route path="/ai-studio" element={<AIStudio />} />
                                      <Route path="/user/:username" element={<UserProfile />} />
                                      <Route path="/articles" element={<Articles />} />
                                      <Route path="/article/:slug" element={<ArticleDetail />} />
                                      <Route path="/live" element={<Live />} />
                                      <Route path="/livestream-events" element={<LivestreamEvents />} />
                                      <Route path="/events" element={<Events />} />
                                      <Route path="/testimonies" element={<Testimonies />} />
                                      <Route path="/prayer-wall" element={<PrayerWall />} />
                                      <Route path="/donate" element={<Donate />} />
                                      <Route path="/subscription" element={<Subscription />} />
                                      <Route path="/tv" element={<TVHome />} />
                                      <Route path="/tv/show/:slug" element={<TVShow />} />
                                      <Route path="/tv/schedule" element={<TVSchedule />} />
                                      <Route path="/tv/events" element={<TVEvents />} />
                                      <Route path="/tv/badges" element={<TVBadges />} />
                                      <Route path="/tv/search" element={<TVSearch />} />

                                      <Route path="/library" element={<AuthGuard requireAuth><Library /></AuthGuard>} />
                                      <Route path="/community" element={<AuthGuard requireAuth><Community /></AuthGuard>} />
                                      <Route path="/rewards" element={<AuthGuard requireAuth><Rewards /></AuthGuard>} />
                                      <Route path="/settings" element={<AuthGuard requireAuth><Settings /></AuthGuard>} />
                                      <Route path="/submit-music" element={<AuthGuard requireAuth><SubmitMusic /></AuthGuard>} />
                                      <Route path="/checkout" element={<AuthGuard requireAuth><Checkout /></AuthGuard>} />
                                      <Route path="/watch-history" element={<AuthGuard requireAuth><WatchHistory /></AuthGuard>} />
                                      <Route path="/affiliate" element={<AuthGuard requireAuth><AffiliateDashboard /></AuthGuard>} />
                                      <Route path="/admin-revenue" element={<AuthGuard requireAuth><AdminRevenue /></AuthGuard>} />
                                      <Route path="/distribution" element={<AuthGuard requireAuth><Distribution /></AuthGuard>} />
                                      <Route path="/ministry" element={<AuthGuard requireAuth><Ministry /></AuthGuard>} />
                                      <Route path="/command-center" element={<AuthGuard requireAuth><CommandCenter /></AuthGuard>} />
                                      <Route path="/brand-dashboard" element={<AuthGuard requireAuth><BrandDashboard /></AuthGuard>} />
                                      <Route path="/tv/creator" element={<AuthGuard requireAuth><TVCreator /></AuthGuard>} />
                                      <Route path="/create-playlist" element={<AuthGuard requireAuth><CreatePlaylist /></AuthGuard>} />
                                      <Route path="/artist/pending" element={<AuthGuard requireArtist><ArtistPending /></AuthGuard>} />
                                      <Route path="/artist/dashboard" element={<AuthGuard requireArtist requireApprovedArtist><ArtistDashboard /></AuthGuard>} />
                                      <Route path="/artist/upload" element={<AuthGuard requireArtist requireApprovedArtist><UploadMusic /></AuthGuard>} />
                                      <Route path="/artist/upload-song" element={<AuthGuard requireArtist requireApprovedArtist><UploadSong /></AuthGuard>} />
                                      <Route path="/artist/upload-album" element={<AuthGuard requireArtist requireApprovedArtist><UploadAlbum /></AuthGuard>} />
                                      <Route path="/artist/upload-video" element={<AuthGuard requireArtist requireApprovedArtist><UploadVideo /></AuthGuard>} />
                                      <Route path="/creator-portal" element={<AuthGuard requireArtist requireApprovedArtist><CreatorPortal /></AuthGuard>} />

                                      <Route path="/:slug" element={<ArtistProfile />} />
                                      <Route path="*" element={<NotFound />} />
                                    </Route>
                                  </Routes>
                                </Suspense>
                              </GuestProvider>
                            </SocialProvider>
                          </VideoProvider>
                        </PlayerProvider>
                      </CartProvider>
                    </UploadQueueProvider>
                  </ShareProvider>
                </ReferralProvider>
              </PricingProvider>
            </UserProvider>
          </AuthProvider>
        </ErrorBoundary>
      </Router>
    </HelmetProvider>
  );
}

export default App;
